import React from 'react';
import { Box, Typography, Grid, Paper, useTheme } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { CostOverview, UtilityType } from '../../../types/utility.types';
import ElectricityIcon from '@mui/icons-material/ElectricBolt';
import GasIcon from '@mui/icons-material/LocalFireDepartment';
import WaterIcon from '@mui/icons-material/Water';
import PhoneIcon from '@mui/icons-material/Phone';
import DeleteIcon from '@mui/icons-material/Delete';
import PaymentsIcon from '@mui/icons-material/Payments';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import EvStationIcon from '@mui/icons-material/EvStation';
import NatureIcon from '@mui/icons-material/Nature';
import SecurityIcon from '@mui/icons-material/Security';
import EngineeringIcon from '@mui/icons-material/Engineering';

interface CostOverviewChartProps {
  data: CostOverview[];
}

// Helper function to get service type display name and icon
const getServiceTypeInfo = (type: string) => {
  const serviceTypeMap: Record<string, { label: string, icon: JSX.Element }> = {
    'Gas': { label: 'Gas', icon: <GasIcon color="primary" /> },
    'Electricity': { label: 'Electricity', icon: <ElectricityIcon color="primary" /> },
    'Water': { label: 'Water', icon: <WaterIcon color="primary" /> },
    'Telecoms': { label: 'Telecoms', icon: <PhoneIcon color="primary" /> },
    'Waste': { label: 'Waste', icon: <DeleteIcon color="primary" /> },
    'Merchant Services': { label: 'Merchant Services', icon: <PaymentsIcon color="primary" /> },
    'PPA Export Contract': { label: 'PPA Export', icon: <BatteryChargingFullIcon color="primary" /> },
    'EV': { label: 'EV', icon: <EvStationIcon color="primary" /> },
    'Renewable': { label: 'Renewable', icon: <NatureIcon color="primary" /> },
    'Insurance': { label: 'Insurance', icon: <SecurityIcon color="primary" /> },
    'New Connection': { label: 'New Connection', icon: <EngineeringIcon color="primary" /> }
  };

  return serviceTypeMap[type] || { label: type || 'Unknown', icon: <EngineeringIcon color="primary" /> };
};

const CostOverviewChart: React.FC<CostOverviewChartProps> = ({ data }) => {
  const theme = useTheme();
  
  // Format currency values
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };
  
  // Process and clean the data
  const processedData = data
    .filter(item => {
      // Filter out items with missing serviceType or zero/invalid costs
      const validServiceType = item.serviceType || item._id;
      const validCost = typeof item.totalCost === 'number' && item.totalCost > 0;
      return validServiceType && validCost;
    })
    .map(item => ({
      serviceType: item.serviceType || item._id,
      serviceTypeLabel: getServiceTypeInfo(item.serviceType || item._id).label,
      totalCost: item.totalCost || 0,
      averageCost: item.averageCost || 0,
      count: item.count || 0
    }));

  // Sort by total cost descending
  const sortedData = [...processedData].sort((a, b) => b.totalCost - a.totalCost);
  
  // Calculate totals for summary
  const totalServices = sortedData.reduce((sum, item) => sum + item.count, 0);
  const totalCost = sortedData.reduce((sum, item) => sum + item.totalCost, 0);
  const overallAverageCost = totalServices > 0 ? totalCost / totalServices : 0;
  
  // Define a color palette for the pie chart
  const colorPalette = [
    '#2196f3', // Blue
    '#4caf50', // Green
    '#ff9800', // Orange
    '#f44336', // Red
    '#9c27b0', // Purple
    '#00bcd4', // Cyan
    '#ffeb3b', // Yellow
    '#795548', // Brown
    '#607d8b', // Blue Grey
    '#e91e63', // Pink
    '#3f51b5', // Indigo
  ];

  // Prepare data for pie chart (cost distribution)
  const pieChartData = sortedData.map((item, index) => ({
    id: item.serviceTypeLabel,
    value: item.totalCost,
    label: item.serviceTypeLabel,
    color: colorPalette[index % colorPalette.length]
  }));

  // If no data, show a message
  if (!data || data.length === 0 || sortedData.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: 350 }}>
        <Typography variant="subtitle1" color="text.secondary">
          No commission data available
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, maxWidth: 300, textAlign: 'center' }}>
          This could be due to missing uplift values, EAC/AQ values, or no utility services matching the current filters.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 2, 
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(25,118,210,0.05)',
              borderRadius: 2,
              height: '100%'
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              Total Services
            </Typography>
            <Typography variant="h4" sx={{ mt: 1, fontWeight: 'medium' }}>
              {totalServices}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 2, 
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(25,118,210,0.05)',
              borderRadius: 2,
              height: '100%'
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              Total Commission Value
            </Typography>
            <Typography variant="h4" sx={{ mt: 1, fontWeight: 'medium' }}>
              {formatCurrency(totalCost)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Based on uplift × consumption × duration
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 2, 
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(25,118,210,0.05)',
              borderRadius: 2,
              height: '100%'
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              Average Commission per Service
            </Typography>
            <Typography variant="h4" sx={{ mt: 1, fontWeight: 'medium' }}>
              {formatCurrency(overallAverageCost)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={2}>
        {/* Cost Distribution Pie Chart */}
        <Grid item xs={12} md={5}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Commission Value by Service Type
          </Typography>
          <Box sx={{ height: 250 }}>
            <PieChart
              series={[{
                data: pieChartData,
                highlightScope: { faded: 'global', highlighted: 'item' },
                faded: { innerRadius: 30, additionalRadius: -30, color: 'gray' },
                valueFormatter: (value: any) => formatCurrency(typeof value === 'number' ? value : 0),
              }]}
              height={250}
              margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
              slotProps={{
                legend: {
                  hidden: true
                }
              }}
            />
          </Box>
        </Grid>

        {/* Service Type Breakdown */}
        <Grid item xs={12} md={7}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Service Type Breakdown
          </Typography>
          <Box sx={{ overflowX: 'auto', maxHeight: 250 }}>
            <Grid container spacing={1}>
              {sortedData.map((item, index) => {
                const serviceInfo = getServiceTypeInfo(item.serviceType);
                return (
                  <Grid item xs={12} key={index}>
                    <Paper 
                      elevation={0}
                      sx={{ 
                        p: 1.5, 
                        display: 'flex', 
                        alignItems: 'center',
                        borderRadius: 1,
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(25,118,210,0.05)'
                      }}
                    >
                      <Box sx={{ mr: 1 }}>{serviceInfo.icon}</Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2" fontWeight="medium">
                          {serviceInfo.label}
                        </Typography>
                        <Grid container>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">
                              Services: {item.count}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">
                              Avg: {formatCurrency(item.averageCost)}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                      <Typography variant="body2" fontWeight="medium">
                        {formatCurrency(item.totalCost)}
                      </Typography>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export { CostOverviewChart };

